import type { IOrigin } from '@/types/OrigDest'
import { SDP_EVENT_NAME, SDP_PAGE_TYPE } from '@/utils/constants/dataLayers'

function isDataValid(data: object) {
  if (data === null || typeof data !== 'object' || !Object.values(data).length) {
    return false
  }

  return true
}

export default function useSingleDestinationPageDataLayer(destinCode?: string) {
  const localeStore = useLocaleStore()
  const selectedOrigin = useState<IOrigin | null>('origDest-selected-origin', () => null)
  const firedSectionImpressions = useState<Record<string, true>>('sdp-fired-section-impressions', () => ({}))
  const activePageInstances = useState<Record<string, number>>('sdp-active-page-instances', () => ({}))

  if (import.meta.client) {
    const scopeKey = destinCode ?? ''
    activePageInstances.value[scopeKey] = (activePageInstances.value[scopeKey] ?? 0) + 1

    onUnmounted(() => {
      activePageInstances.value[scopeKey] = (activePageInstances.value[scopeKey] ?? 1) - 1

      if (activePageInstances.value[scopeKey] > 0) return

      Reflect.deleteProperty(activePageInstances.value, scopeKey)

      Object.keys(firedSectionImpressions.value).forEach((impressionKey) => {
        if (impressionKey.startsWith(`${scopeKey}:`))
          Reflect.deleteProperty(firedSectionImpressions.value, impressionKey)
      })
    })
  }

  const base = computed(() => ({
    event: SDP_EVENT_NAME,
    site_language: localeStore.selectedLanguage,
    page_type: SDP_PAGE_TYPE,
    origin_airport_code: selectedOrigin.value?.code ?? '',
    destination_airport_code: destinCode ?? ''
  }))

  function pushSDPDataLayer(data: object) {
    if (!isDataValid(data)) return

    pushToDataLayer({
      ...base.value,
      ...data
    })
  }

  function pushHistogramDataLayer(data: object) {
    pushSDPDataLayer({
      event_category: 'destination_histogram',
      ...data
    })
  }

  function pushHistogramNotReturnedDataLayer(data: object) {
    pushSDPDataLayer({
      event_category: 'destination_histogram',
      event_action: 'not_returned',
      ...data
    })
  }

  function pushOriginChangeDataLayer(data: object) {
    const payload: Record<string, unknown> = {
      ...base.value,
      event_category: 'destination_origin_change',
      event_action: 'dropdown_selection',
      ...data
    }

    delete payload.destination_airport_code

    if (!isDataValid(payload)) return

    pushToDataLayer(payload)
  }

  function pushBookAFlightDataLayer(data: object) {
    pushSDPDataLayer({
      event_category: 'book_a_flight',
      event_action: 'click',
      ...data
    })
  }

  function pushSectionImpressionDataLayer(data: { event_label: string }) {
    const impressionKey = `${destinCode ?? ''}:${data.event_label}`

    if (firedSectionImpressions.value[impressionKey]) return
    firedSectionImpressions.value[impressionKey] = true

    pushSDPDataLayer({
      event_category: 'sections_performance',
      event_action: 'impressions',
      ...data
    })
  }

  function pushDiscoverOtherDestinationsImpressionDataLayer(destinationCode: string) {
    if (!destinCode) return

    const impressionKey = `${destinCode}:discover_other_destinations:${destinationCode}`

    if (firedSectionImpressions.value[impressionKey]) return
    firedSectionImpressions.value[impressionKey] = true

    pushSDPDataLayer({
      event_category: 'sections_performance',
      event_action: 'impressions',
      event_label: 'discover_other_destinations',
      destination_airport_code: destinationCode
    })
  }

  function pushSectionClickDataLayer(data: object) {
    pushSDPDataLayer({
      event_category: 'sections_performance',
      ...data
    })
  }

  function pushCarouselNavigationDataLayer(data: object) {
    pushSDPDataLayer({
      event_category: 'sections_performance',
      event_action: 'carousel_navigation',
      ...data
    })
  }

  function pushAccordionDataLayer(data: object) {
    pushSDPDataLayer({
      event_category: 'sections_performance',
      ...data
    })
  }

  return {
    pushHistogramDataLayer,
    pushHistogramNotReturnedDataLayer,
    pushOriginChangeDataLayer,
    pushBookAFlightDataLayer,
    pushSectionImpressionDataLayer,
    pushDiscoverOtherDestinationsImpressionDataLayer,
    pushSectionClickDataLayer,
    pushCarouselNavigationDataLayer,
    pushAccordionDataLayer
  }
}
